import React, { useState } from 'react';
import styled from 'styled-components';
import { Coupon } from '../types';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useMerchant } from '../context/MerchantContext';
import { useAuth } from '../context/AuthContext';
import { restaurantApi } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  ModalHeader as GlobalModalHeader,
  ModalTitle as GlobalModalTitle,
  CloseButton as GlobalCloseButton
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';
import FormInput from './common/FormInput';
import FormContainer from './common/FormContainer';

interface CouponModalProps {
  isOpen: boolean;
  onClose: () => void;
}


const CouponButton = styled.button`
  background-color: ${props => props.theme.primary || '#e74c3c'};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px ${props => props.theme.primary ? `${props.theme.primary}33` : 'rgba(231, 76, 60, 0.2)'};

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
    box-shadow: 0 6px 12px ${props => props.theme.primary ? `${props.theme.primary}4D` : 'rgba(231, 76, 60, 0.3)'};
  }

  &:disabled {
    background-color: #feb2b2;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CouponInfo = styled.div`
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
`;

const CouponTitle = styled.h3`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.2rem;
`;

const CouponDescription = styled.p`
  margin: 0 0 15px 0;
  color: #666;
  font-size: 1rem;
`;

const CouponDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.9rem;
  color: #666;
`;

const CouponDetail = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 5px;
`;

const CouponModal: React.FC<CouponModalProps> = ({ isOpen, onClose }) => {
  const { state: cartState, applyCoupon } = useCart();
  const { colors } = useTheme();
  const { merchantId } = useMerchant();
  const { user, setShowLoginModal } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(e.target.value.toUpperCase());
    setError(null);
  };

  const validateCoupon = async () => {
    if (!couponCode) {
      setError('Por favor, digite um código de cupom.');
      return;
    }

    if (!merchantId) {
      setError('Erro ao identificar o estabelecimento.');
      return;
    }

    if (!user) {
      setError('Você precisa estar logado para validar um cupom.');
      setShowLoginModal(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if any item is missing categoryId and try to alert the user
      const itemWithMissingCategory = cartState.items.find(item => !item.categoryId);
      if (itemWithMissingCategory) {
        setError(`O item "${itemWithMissingCategory.name}" está sem categoria vinculada. Por favor, remova-o e adicione-o novamente ao carrinho.`);
        setLoading(false);
        return;
      }

      // Prepare items for validation
      const items = cartState.items.map(item => ({
        productId: item.menuItemId,
        categoryId: item.categoryId as string,
        price: item.price,
        quantity: item.quantity
      }));

      const response = await restaurantApi.validateCoupon(merchantId, {
        code: couponCode,
        customerId: user.id,
        items: items
      });

      if (response.valid) {
        const newCoupon: Coupon = {
          code: response.code,
          isValid: true,
          discountAmount: response.discountAmount,
        };
        setCoupon(newCoupon);
      } else {
        setError(response.message || 'Cupom inválido ou não aplicável para este pedido.');
        setCoupon(null);
      }
    } catch (err) {
      console.error('Error validating coupon:', err);
      setError('Erro ao validar o cupom. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateCoupon();
  };

  const handleApplyCoupon = () => {
    if (coupon) {
      applyCoupon(coupon);
      onClose();
    }
  };

  // Format price to Brazilian Real
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} zIndex={10500}>
        <GlobalModalHeader>
          <div style={{ width: '32px' }} />
          <GlobalModalTitle>Cupom de Desconto</GlobalModalTitle>
          <GlobalCloseButton onClick={onClose}>×</GlobalCloseButton>
        </GlobalModalHeader>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 2.5rem 2.5rem' }}>
          <FormContainer as="form" onSubmit={handleSubmit}>
            <FormInput
              label="Código do Cupom"
              type="text"
              placeholder="Digite o código do cupom"
              value={couponCode}
              onChange={handleCouponChange}
            />
            <CouponButton theme={colors} type="submit" disabled={loading}>
              {loading ? 'Verificando...' : 'Verificar'}
            </CouponButton>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </FormContainer>

          {coupon && (
            <CouponInfo theme={colors}>
              <CouponTitle theme={colors}>Cupom: {coupon.code}</CouponTitle>
              <CouponDescription theme={colors}>
                {coupon.discountAmount !== undefined && coupon.discountAmount > 0
                  ? `${formatPrice(coupon.discountAmount)} de desconto no seu pedido`
                  : coupon.discountPercentage 
                    ? `${coupon.discountPercentage}% de desconto no valor total`
                    : coupon.discountValue 
                      ? `${formatPrice(coupon.discountValue)} de desconto no valor total`
                      : 'Desconto aplicável ao pedido'}
              </CouponDescription>
              <CouponDetails theme={colors}>
                {coupon.minOrderValue && (
                  <CouponDetail theme={colors}>
                    <span>Valor mínimo do pedido:</span>
                    <span>{formatPrice(coupon.minOrderValue)}</span>
                  </CouponDetail>
                )}
                {coupon.expiryDate && (
                  <CouponDetail theme={colors}>
                    <span>Válido até:</span>
                    <span>{new Date(coupon.expiryDate).toLocaleDateString('pt-BR')}</span>
                  </CouponDetail>
                )}
              </CouponDetails>
              <CouponButton 
                theme={colors}
                onClick={handleApplyCoupon}
                style={{ marginTop: '15px' }}
              >
                Aplicar Cupom
              </CouponButton>
            </CouponInfo>
          )}
        </div>
    </Modal>
  );
};

export default CouponModal;